import { useCallback, useMemo } from 'react';

// 節點數據管理 Hook
export const useNodeData = (isReady, t) => {
  // 在組件內部定義 defaultNodeData 函數，這樣可以使用 t() 函數
  const defaultNodeData = useCallback((type) => {
    if (!isReady) {
      // 語言系統未準備好時使用英文
      switch (type) {
        case 'start':
          return { 
            taskName: 'Start',
            activationType: 'manual',
            scheduledTable: '',
            scheduledQuery: '',
            scheduledDataSetId: '',
            scheduledQueryConditionGroups: [],
            scheduledMappedFields: [],
            scheduledPostSuccessOperationData: {},
            scheduledPostSuccessOperationDataFields: [],
            scheduledInterval: 300,
            emailProviderKey: '',
            emailMonitorMailbox: '',
            emailFilterSender: '',
            emailFilterSubject: '',
            emailFilterBodyKeywords: '',
            emailFilterKeywordsRequired: '',
            emailFilterSenderNot: '',
            emailFilterSubjectNot: '',
            emailFilterBodyKeywordsNot: '',
            emailFilterHasAttachment: false,
            emailFilterAttachmentExtensions: '',
            emailFilterAttachmentFileName: '',
            emailFilterAttachmentFileNameNot: ''
          };
        case 'end':
          return { taskName: 'End' };
        case 'sendWhatsApp':
          return { taskName: 'Send Message', message: '', to: '' };
        case 'sendWhatsAppTemplate':
          return { taskName: 'Send WhatsApp Template', templateId: '', templateName: '', variables: {} };
        case 'sendWhatsAppShoppingCart':
          return { taskName: 'WhatsApp Shopping Cart', shoppingCartMode: 'select_collection', collectionId: null, body: '', header: '', footer: '', to: '', recipientDetails: null };
        case 'sendInstitutionOrderingCart':
          return { taskName: 'Institution Ordering Cart', flowTemplateName: 'institution_ordering_cart', flowLanguage: 'zh_TW', to: '', recipientDetails: null };
        case 'chatOrderingCart':
          return {
            taskName: 'Chat Shopping Cart',
            replyType: 'initiator',
            specifiedUsers: '',
            recipientDetails: null,
            targetCompanyId: '',
            productLanguage: 'zh_TC',
            aiProviderKey: '',
            aiStaffId: null,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            initialGreetingMessage: '',
            reconfirmQuestion: '',
            missingInfoPromptExtra: '',
            additionalPrompt: '',
            endKeywords: [
              '取消', '結束', '停止', '不下單', '不要了', '不買了', '算了', '不用了', '先不要', '下次再說', '不訂了', '不用下單',
              'cancel', 'stop', 'end', 'quit', 'never mind', 'no order', 'not ordering', "don't order", 'stop ordering', 'abort', 'call it off'
            ],
            btnConfirmLabel: '',
            btnModifyLabel: '',
            btnCancelLabel: '',
            systemPromptOverride: '',
            validation: {
              timeoutCompleteIsActive: false,
              timeoutCompleteDays: 0,
              timeoutCompleteHours: 0,
              timeoutCompleteMinutes: 0
            }
          };
        case 'chatBot':
          return {
            taskName: 'Chat Bot',
            replyType: 'initiator',
            specifiedUsers: '',
            recipientDetails: null,
            aiProviderKey: '',
            aiStaffId: null,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            initialGreetingMessage: '',
            endMessage: '',
            ragAdditionalQuery: '',
            additionalPrompt: '',
            endKeywords: [
              '取消', '結束', '停止', '不用了', '先不要', '下次再說',
              'cancel', 'stop', 'end', 'quit', 'never mind', 'no thanks'
            ],
            systemPromptOverride: '',
            validation: {
              timeoutCompleteIsActive: false,
              timeoutCompleteDays: 0,
              timeoutCompleteHours: 0,
              timeoutCompleteMinutes: 0,
              aiCompleteBatchCountEnabled: false,
              aiCompleteBatchCount: 1
            }
          };
        case 'waitForQRCode':
          return {
            taskName: 'Wait for QR Code',
            qrCodeVariable: '',
            message: 'Please scan the QR code',
            timeout: 300
          };
        case 'waitForLocation':
          return {
            taskName: '請求地理位置',
            replyType: 'initiator',
            message: 'Please enter your reply',
            messageMode: 'direct',
            templateId: '',
            templateName: '',
            isMetaTemplate: false,
            templateLanguage: null,
            templateVariables: [],
            locationRequestBody: 'Please share your location.',
            locationVariable: '',
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            validation: {
              enabled: true,
              validatorType: 'default',
              prompt: 'Please enter valid content',
              retryMessage: 'Input incorrect, please retry',
              maxRetries: 3,
            },
          };
        case 'waitForEmailReply':
          return {
            taskName: 'Wait for Email Reply',
            emailProviderKey: '',
            emailMonitorMailbox: '',
            emailFilterSender: '',
            emailFilterSubject: '',
            emailFilterBodyKeywords: '',
            emailFilterKeywordsRequired: '',
            emailFilterSenderNot: '',
            emailFilterSubjectNot: '',
            emailFilterBodyKeywordsNot: '',
            emailFilterHasAttachment: false,
            emailFilterAttachmentExtensions: '',
            emailFilterAttachmentFileName: '',
            emailFilterAttachmentFileNameNot: '',
            validation: {
              enabled: false,
              aiIsActive: false,
              timeIsActive: false,
              validatorType: 'default',
              prompt: 'Please validate this email reply',
              retryMessage: 'Validation failed, waiting for another email',
              maxRetries: 3,
              retryIntervalDays: 0,
              retryIntervalHours: 0,
              retryIntervalMinutes: 10,
              retryLimitValue: 5,
              autoCompleteOnTimeout: false,
              autoCompleteTimeoutDays: 0,
              autoCompleteTimeoutHours: 0,
              autoCompleteTimeoutMinutes: 0
            }
          };
        case 'switch':
          return {
            taskName: 'Switch',
            conditionGroups: [
              { id: 'group1', relation: 'and', conditions: [], outputPath: '' }
            ],
            defaultPath: 'default'
          };
        case 'waitReply':
          return { 
            taskName: 'Wait for User Reply', 
            replyType: 'initiator',
            specifiedUsers: '',
            message: 'Please enter your reply',
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            validation: {
              enabled: true,
              validatorType: 'default',
              prompt: 'Please enter valid content',
              retryMessage: 'Input incorrect, please retry',
              maxRetries: 3,
              aiStaffId: null
            }
          };
        case 'dataSetQuery':
          return { taskName: 'DataSet Query/Update', dataSetId: '', operationType: 'SELECT', queryConditionGroups: [], operationData: {}, mappedFields: [] };
        case 'processDataCell':
          return { taskName: 'Process Data Cell', assignmentRules: [] };
        case 'callApi':
          return { taskName: 'Trigger External API', url: '' };
        case 'sendEForm':
          return { 
            taskName: 'Send eForm', 
            formName: '', 
            formId: '', 
            formDescription: '', 
            to: '', 
            approvalResultVariable: '', 
            approveButtonLabel: '',
            rejectButtonLabel: '',
            messageTemplate: '', 
            useCustomMessage: false,
            sendEFormMode: 'integrateWaitReply',
            integrateEmailStartSendToAi: 'attachment',
            multiFormMode: 'single',
            integratedDataSetQueryNodeId: '',
            integratedEFormNodeId: '',
            fillFormAdditionalPrompt: '',
            additionalPrompt: '',
            jsonResultVariable: '',
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            templateVariables: []
          };
        case 'generateHtmlReport':
          return {
            taskName: 'Generate HTML Report',
            generationMode: 'integrateWaitReply',
            integrateEmailStartSendToAi: 'attachment',
            attachmentBatchMode: 'all',
            attachmentAiInputFormat: 'any',
            integratedDataSetQueryNodeId: '',
            fillFormAdditionalPrompt: '',
            additionalPrompt: '',
            jsonResultVariable: '',
            aiProviderKey: '',
            aiStaffId: null,
            sendNotificationToRecipients: true,
            messageMode: 'direct',
            message: '',
            messageTemplate: 'Your report {reportName} is ready:\n\n{reportUrl}',
            emailConfig: {},
            to: '',
            recipientDetails: null,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            embeddedImages: []
          };
        case 'generateDocument':
          return {
            taskName: 'Generate Document',
            generationMode: 'integrateWaitReply',
            integrateEmailStartSendToAi: 'attachment',
            attachmentBatchMode: 'all',
            attachmentAiInputFormat: 'any',
            integratedDataSetQueryNodeId: '',
            fillFormAdditionalPrompt: '',
            additionalPrompt: '',
            jsonResultVariable: '',
            aiProviderKey: '',
            aiStaffId: null,
            documentOutputFormat: 'docx',
            documentStorageKind: 'Local',
            sendNotificationToRecipients: true,
            messageMode: 'direct',
            message: '',
            messageTemplate: 'Your document {docName} is ready:\n\n{docUrl}',
            emailConfig: {},
            to: '',
            recipientDetails: null,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: [],
            embeddedImages: []
          };
        case 'calendar':
          return {
            taskName: 'Calendar',
            generationMode: 'integrateWaitReply',
            integrateEmailStartSendToAi: 'attachment',
            attachmentBatchMode: 'all',
            attachmentAiInputFormat: 'any',
            integratedDataSetQueryNodeId: '',
            integratedEFormNodeId: '',
            fillCalendarAdditionalPrompt: '',
            additionalPrompt: '',
            calendarCommandVariable: '',
            aiProviderKey: '',
            aiStaffId: null,
            defaultWorkflowDefinitionLinkage: true,
            dryRun: false,
            failOnValidationError: false,
            // 兩段式 AI（分析視窗 + 現況快照）
            disableTwoStage: false,
            analysisLookBackDaysDefault: 30,
            analysisLookAheadDaysDefault: 365,
            analysisLookBackDaysMax: 90,
            analysisLookAheadDaysMax: 1095,
            snapshotMaxEvents: 500,
            snapshotIncludeEvents: true,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: []
          };
        case 'odoo':
          return {
            taskName: 'Odoo',
            apiProviderKey: 'odoo',
            operationType: 'createSO',
            generationMode: 'formBased',
            integrateEmailStartSendToAi: 'attachment',
            attachmentBatchMode: 'all',
            attachmentAiInputFormat: 'any',
            integratedDataSetQueryNodeId: '',
            mappedFields: [],
            json: '',
            fillOdooAdditionalPrompt: '',
            additionalPrompt: '',
            aiProviderKey: '',
            aiStaffId: null,
            odooCommandVariable: '',
            odooResultVariable: '',
            defaultCompanyId: null,
            autoConfirm: true,
            autoPost: false,
            autoValidate: false,
            dryRun: false,
            failOnValidationError: false,
            knowledgeBaseEnabled: false,
            knowledgeBaseDataSetId: '',
            knowledgeBaseQueryConditionGroups: [],
            knowledgeStudioDocumentIds: []
          };
        default:
          return { taskName: type };
      }
    }
    
    // 語言系統準備好時使用翻譯
    switch (type) {
      case 'start':
        return { 
          taskName: t('workflowDesigner.defaultStartNode'),
          activationType: 'manual', // manual, webhook, scheduled, email
          whatsAppMenuName: '', // 當 activationType 為 webhook 時，顯示在 WhatsApp 選單的名稱（可與流程名稱不同）
          scheduledTable: '',
          scheduledQuery: '',
          scheduledDataSetId: '',
          scheduledQueryConditionGroups: [],
          scheduledMappedFields: [],
          scheduledPostSuccessOperationData: {},
          scheduledPostSuccessOperationDataFields: [],
          scheduledInterval: 300,
          emailProviderKey: '',
          emailMonitorMailbox: '',
          emailFilterSender: '',
          emailFilterSubject: '',
          emailFilterBodyKeywords: '',
          emailFilterKeywordsRequired: '',
          emailFilterSenderNot: '',
          emailFilterSubjectNot: '',
          emailFilterBodyKeywordsNot: '',
          emailFilterHasAttachment: false,
          emailFilterAttachmentExtensions: '',
          emailFilterAttachmentFileName: '',
          emailFilterAttachmentFileNameNot: ''
        };
      case 'end':
        return { taskName: t('workflowDesigner.defaultEndNode') };
      case 'sendWhatsApp':
        return { taskName: t('workflowDesigner.defaultSendWhatsAppNode'), message: '', to: '' };
      case 'sendWhatsAppTemplate':
        return { taskName: t('workflowDesigner.defaultSendTemplateNode'), templateId: '', templateName: '', variables: {} };
      case 'sendWhatsAppShoppingCart':
        return { taskName: t('workflowDesigner.shoppingCartNode'), shoppingCartMode: 'select_collection', collectionId: null, body: '', header: '', footer: '', to: '', recipientDetails: null };
      case 'sendInstitutionOrderingCart':
        return { taskName: t('workflowDesigner.institutionOrderingCartNode'), flowTemplateName: 'institution_ordering_cart', flowLanguage: 'zh_TW', to: '', recipientDetails: null };
      case 'chatOrderingCart':
        return {
          taskName: t('workflowDesigner.chatOrderingCartNode') || 'Chat Shopping Cart',
          replyType: 'initiator',
          specifiedUsers: '',
          recipientDetails: null,
          targetCompanyId: '',
          productLanguage: 'zh_TC',
          aiProviderKey: '',
          aiStaffId: null,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          initialGreetingMessage: '',
          reconfirmQuestion: '',
          missingInfoPromptExtra: '',
          additionalPrompt: '',
          endKeywords: [
            '取消', '結束', '停止', '不下單', '不要了', '不買了', '算了', '不用了', '先不要', '下次再說', '不訂了', '不用下單',
            'cancel', 'stop', 'end', 'quit', 'never mind', 'no order', 'not ordering', "don't order", 'stop ordering', 'abort', 'call it off'
          ],
          btnConfirmLabel: '',
          btnModifyLabel: '',
          btnCancelLabel: '',
          systemPromptOverride: ''
        };
      case 'chatBot':
        return {
          taskName: t('workflowDesigner.chatBotNode') || 'Chat Bot',
          replyType: 'initiator',
          specifiedUsers: '',
          recipientDetails: null,
          aiProviderKey: '',
          aiStaffId: null,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          initialGreetingMessage: '',
          endMessage: '',
          ragAdditionalQuery: '',
          additionalPrompt: '',
          endKeywords: [
            '取消', '結束', '停止', '不用了', '先不要', '下次再說',
            'cancel', 'stop', 'end', 'quit', 'never mind', 'no thanks'
          ],
          systemPromptOverride: '',
          validation: {
            timeoutCompleteIsActive: false,
            timeoutCompleteDays: 0,
            timeoutCompleteHours: 0,
            timeoutCompleteMinutes: 0,
            aiCompleteBatchCountEnabled: false,
            aiCompleteBatchCount: 1
          }
        };
      case 'waitReply':
        return { 
          taskName: t('workflowDesigner.defaultWaitReplyNode'), 
          replyType: 'initiator',
          specifiedUsers: '',
          message: t('workflowDesigner.defaultReplyMessage'),
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          validation: {
            enabled: true,
            validatorType: 'default',
            prompt: t('workflowDesigner.defaultValidationPrompt'),
            retryMessage: t('workflowDesigner.defaultRetryMessage'),
            maxRetries: 3,
            aiStaffId: null
          }
        };
      case 'waitForQRCode':
        return { 
          taskName: t('workflowDesigner.defaultWaitForQRCodeNode'), 
          qrCodeVariable: '', // 存儲 QR Code 值的流程變量
          message: t('workflowDesigner.defaultQRCodeMessage'),
          timeout: 300 // 超時時間（秒）
        };
      case 'waitForLocation':
        return {
          taskName: t('workflowDesigner.defaultWaitForLocationNode'),
          replyType: 'initiator',
          message: t('workflowDesigner.defaultReplyMessage'),
          messageMode: 'direct',
          templateId: '',
          templateName: '',
          isMetaTemplate: false,
          templateLanguage: null,
          templateVariables: [],
          locationRequestBody: t('workflowDesigner.defaultLocationRequestBody'),
          locationVariable: '',
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          validation: {
            enabled: true,
            validatorType: 'default',
            prompt: t('workflowDesigner.defaultValidationPrompt'),
            retryMessage: t('workflowDesigner.defaultRetryMessage'),
            maxRetries: 3,
          },
        };
      case 'waitForEmailReply':
        return {
          taskName: t('workflowDesigner.defaultWaitForEmailReplyNode') || 'Wait for Email Reply',
          emailProviderKey: '',
          emailMonitorMailbox: '',
          emailFilterSender: '',
          emailFilterSubject: '',
          emailFilterBodyKeywords: '',
          emailFilterKeywordsRequired: '',
          emailFilterSenderNot: '',
          emailFilterSubjectNot: '',
          emailFilterBodyKeywordsNot: '',
          emailFilterHasAttachment: false,
          emailFilterAttachmentExtensions: '',
          emailFilterAttachmentFileName: '',
          emailFilterAttachmentFileNameNot: '',
          validation: {
            enabled: false,
            aiIsActive: false,
            timeIsActive: false,
            validatorType: 'default',
            prompt: t('workflowDesigner.defaultValidationPrompt'),
            retryMessage: t('workflowDesigner.defaultRetryMessage'),
            maxRetries: 3,
            retryIntervalDays: 0,
            retryIntervalHours: 0,
            retryIntervalMinutes: 10,
            retryLimitValue: 5,
            autoCompleteOnTimeout: false,
            autoCompleteTimeoutDays: 0,
            autoCompleteTimeoutHours: 0,
            autoCompleteTimeoutMinutes: 0
          }
        };
      case 'switch':
        return { 
          taskName: t('workflowDesigner.defaultSwitchNode'), 
          conditionGroups: [
            {
              id: 'group1',
              relation: 'and',
              conditions: [],
              outputPath: ''
            }
          ],
          defaultPath: 'default'
        };
      case 'dataSetQuery':
        return { taskName: t('workflowDesigner.defaultDataSetQueryNode'), dataSetId: '', operationType: 'SELECT', queryConditionGroups: [], operationData: {}, mappedFields: [] };
      case 'processDataCell':
        return { taskName: t('workflowDesigner.defaultProcessDataCellNode') || '整理流程資料', assignmentRules: [] };
      case 'callApi':
        return { taskName: t('workflowDesigner.defaultCallApiNode'), url: '' };
      case 'sendEForm':
        return { 
          taskName: t('workflowDesigner.defaultSendEFormNode'), 
          formName: '', 
          formId: '', 
          formDescription: '', 
          to: '', 
          approvalResultVariable: '',
          approveButtonLabel: '',
          rejectButtonLabel: '',
          messageTemplate: t('workflowDesigner.sendEForm.defaultNotificationMessage'),
          useCustomMessage: false,
          sendEFormMode: 'integrateWaitReply',
          integrateEmailStartSendToAi: 'attachment',
          multiFormMode: 'single',
          integratedDataSetQueryNodeId: '',
          fillFormAdditionalPrompt: '',
          additionalPrompt: '',
          jsonResultVariable: '',
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          templateVariables: []
        };
      case 'generateHtmlReport':
        return {
          taskName: t('workflowDesigner.defaultGenerateHtmlReportNode'),
          generationMode: 'integrateWaitReply',
          integrateEmailStartSendToAi: 'attachment',
          attachmentBatchMode: 'all',
          attachmentAiInputFormat: 'any',
          integratedDataSetQueryNodeId: '',
          fillFormAdditionalPrompt: '',
          additionalPrompt: '',
          jsonResultVariable: '',
          aiProviderKey: '',
          sendNotificationToRecipients: true,
          messageMode: 'direct',
          message: '',
          messageTemplate: t('workflowDesigner.artifact.defaultHtmlReportNotification'),
          emailConfig: {},
          to: '',
          recipientDetails: null,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          embeddedImages: []
        };
      case 'generateDocument':
        return {
          taskName: t('workflowDesigner.defaultGenerateDocumentNode'),
          generationMode: 'integrateWaitReply',
          integrateEmailStartSendToAi: 'attachment',
          attachmentBatchMode: 'all',
          attachmentAiInputFormat: 'any',
          integratedDataSetQueryNodeId: '',
          fillFormAdditionalPrompt: '',
          additionalPrompt: '',
          jsonResultVariable: '',
          aiProviderKey: '',
          documentOutputFormat: 'docx',
          documentStorageKind: 'Local',
          sendNotificationToRecipients: true,
          messageMode: 'direct',
          message: '',
          messageTemplate: t('workflowDesigner.artifact.defaultDocumentNotification'),
          emailConfig: {},
          to: '',
          recipientDetails: null,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: [],
          embeddedImages: []
        };
      case 'calendar':
        return {
          taskName: t('workflowDesigner.defaultCalendarNode') || 'Calendar',
          generationMode: 'integrateWaitReply',
          integrateEmailStartSendToAi: 'attachment',
          attachmentBatchMode: 'all',
          attachmentAiInputFormat: 'any',
          integratedDataSetQueryNodeId: '',
          integratedEFormNodeId: '',
          fillCalendarAdditionalPrompt: '',
          additionalPrompt: '',
          calendarCommandVariable: '',
          aiProviderKey: '',
          aiStaffId: null,
          defaultWorkflowDefinitionLinkage: true,
          dryRun: false,
          failOnValidationError: false,
          disableTwoStage: false,
          analysisLookBackDaysDefault: 30,
          analysisLookAheadDaysDefault: 365,
          analysisLookBackDaysMax: 90,
          analysisLookAheadDaysMax: 1095,
          snapshotMaxEvents: 500,
          snapshotIncludeEvents: true,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: []
        };
      case 'odoo':
        return {
          taskName: t('workflowDesigner.defaultOdooNode') || 'Odoo',
          apiProviderKey: 'odoo',
          operationType: 'createSO',
          generationMode: 'formBased',
          integrateEmailStartSendToAi: 'attachment',
          attachmentBatchMode: 'all',
          attachmentAiInputFormat: 'any',
          integratedDataSetQueryNodeId: '',
          mappedFields: [],
          json: '',
          fillOdooAdditionalPrompt: '',
          additionalPrompt: '',
          aiProviderKey: '',
          aiStaffId: null,
          odooCommandVariable: '',
          odooResultVariable: '',
          defaultCompanyId: null,
          autoConfirm: true,
          autoPost: false,
          autoValidate: false,
          dryRun: false,
          failOnValidationError: false,
          knowledgeBaseEnabled: false,
          knowledgeBaseDataSetId: '',
          knowledgeBaseQueryConditionGroups: [],
          knowledgeStudioDocumentIds: []
        };
      default:
        return { taskName: type };
    }
  }, [isReady, t]);

  return {
    defaultNodeData
  };
};
